import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banner, Button, SimpleTable, Tag, ckConfirm, useCkToast } from "@NP-Webber/crm-kit/dist/next/index.js";
import { recycleBinApi, type RecycleEntryDto } from "../../api/recycleBinApi";
import { notifyDataChanged } from "../../next/events";

const fmt = (iso: string) => new Date(iso).toLocaleString("he-IL", { day: "numeric", month: "numeric", year: "2-digit", hour: "2-digit", minute: "2-digit" });
const daysLeft = (iso: string) => Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000));

/** «סל מחזור» — what was removed in the last 30 days, restorable by whoever may delete it (ADR-0032). */
export function RecycleBinPanel() {
  const navigate = useNavigate();
  const toast = useCkToast();
  const [items, setItems] = useState<RecycleEntryDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    recycleBinApi
      .list()
      .then((rows) => {
        setItems(rows);
        setError(null);
      })
      .catch((e: unknown) => setError((e as { message?: string })?.message || "לא הצלחנו לטעון את סל המחזור"));
  }, []);
  useEffect(load, [load]);

  const restore = async (row: RecycleEntryDto) => {
    setBusy(row.id);
    try {
      const res = await recycleBinApi.restore(row.id);
      setItems((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
      notifyDataChanged(row.kind);
      toast(`«${row.label}» שוחזר`, {
        tone: "success",
        action: { label: "מעבר למסך", onClick: () => navigate(res.path) },
      });
      if (row.kind === "automation") toast("האוטומציה חזרה מושהית — אפשר להפעיל אותה במסך האוטומציות");
      if (row.kind === "form_intake") toast("קליטת הטפסים חזרה כבויה — אפשר להפעיל אותה במסך הטפסים");
    } catch (e) {
      toast.error((e as { message?: string })?.message || "השחזור נכשל");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row: RecycleEntryDto) => {
    const hard = row.kind === "task" || row.kind === "automation";
    const ok = await ckConfirm({
      title: "הסרה מסל המחזור",
      message: hard ? `«${row.label}» יימחק לצמיתות ולא יהיה אפשר לשחזר אותו.` : `«${row.label}» יוסר מהסל ולא יופיע כאן יותר.`,
      confirmLabel: "הסרה",
      danger: true,
    });
    if (!ok) return;
    setBusy(row.id);
    try {
      await recycleBinApi.remove(row.id);
      setItems((prev) => (prev ? prev.filter((r) => r.id !== row.id) : prev));
    } catch (e) {
      toast.error((e as { message?: string })?.message || "ההסרה נכשלה");
    } finally {
      setBusy(null);
    }
  };

  if (error) return <Banner tone="danger">{error}</Banner>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <SimpleTable
        ariaLabel="סל מחזור"
        loading={!items}
        rows={items ?? []}
        rowKey="id"
        empty="סל המחזור ריק"
        columns={[
          { key: "kindTitle", label: "סוג", render: (r: RecycleEntryDto) => <Tag tone="neutral">{r.kindTitle}</Tag> },
          { key: "label", label: "שם" },
          { key: "deletedBy", label: "נמחק על ידי", render: (r: RecycleEntryDto) => r.deletedBy ?? "—" },
          { key: "deletedAt", label: "מתי", render: (r: RecycleEntryDto) => fmt(r.deletedAt) },
          { key: "purgeAfter", label: "נשאר", align: "end", render: (r: RecycleEntryDto) => `${daysLeft(r.purgeAfter)} ימים` },
          {
            key: "actions",
            label: "",
            align: "end",
            render: (r: RecycleEntryDto) => (
              <span style={{ display: "inline-flex", gap: 6 }}>
                <Button size="sm" icon="undo" disabled={busy === r.id} onClick={() => void restore(r)}>
                  שחזור
                </Button>
                <Button size="sm" variant="ghost" icon="trash" disabled={busy === r.id} onClick={() => void remove(r)} aria-label="הסרה מהסל" iconOnly />
              </span>
            ),
          },
        ]}
      />
      <small style={{ color: "var(--ck-text-3)" }}>
        משימות, אוטומציות, טפסים וקליטות טפסים שנמחקו נשמרים כאן 30 יום. רואים רק פריטים שמותר לכם למחוק.
      </small>
    </div>
  );
}
